import { mui } from "@/mui";
import { XIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState, useCallback } from "react";
import { ed25519 } from "@noble/curves/ed25519";
import { hexToBytes } from "@noble/curves/abstract/utils";
import { useSession, useMobyDB } from "./provider";
import type { Session } from "@/moby/repo";
import { getEmailAccountsFromDB, saveAllEmailAccountsToDB, deleteEmailAccountFromDB, saveEmailAccountToDB } from "@/services/email-account-store";
import { getProviderLogo } from "./email-provider-logos";

// Base64 encoding compatible with tweetnacl-util's decodeBase64
function encodeBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function getLocalPreference(key: string): string | null {
  try {
    return localStorage.getItem(`moby_pref_${key}`);
  } catch {
    return null;
  }
}

function setLocalPreference(key: string, value: string): void {
  try {
    localStorage.setItem(`moby_pref_${key}`, value);
  } catch (error) {
    console.error(`Failed to save preference ${key}:`, error);
  }
}

export interface EmailAccount {
  id: string;
  email: string;
  imapHost: string;
  imapPort: number;
  smtpHost: string;
  smtpPort: number;
  username: string;
  password: string;
  color: string;
  enabled: boolean;
  // OAuth2 support
  authType: "password" | "oauth2";
  accessToken?: string;
  refreshToken?: string;
  tokenExpiry?: number; // Unix timestamp
  // MobyChat registration (opt-in directory listing)
  registeredWithMobyChat?: boolean;
  isPrimary?: boolean;
}

const DEFAULT_COLORS = [
  "#F44336", "#E91E63", "#FF9800", "#FFC107",
  "#4CAF50", "#009688", "#00BCD4", "#2196F3",
  "#3F51B5", "#673AB7", "#9C27B0", "#795548",
  "#607D8B", "#9E9E9E", "#EF9A9A", "#90CAF9",
  "#CE93D8", "#A5D6A7",
];

// Auto-detect email provider settings based on domain
const EMAIL_PROVIDER_SETTINGS: Record<string, { imapHost: string; imapPort: number; smtpHost: string; smtpPort: number }> = {
  "gmail.com": { imapHost: "imap.gmail.com", imapPort: 993, smtpHost: "smtp.gmail.com", smtpPort: 587 },
  "googlemail.com": { imapHost: "imap.gmail.com", imapPort: 993, smtpHost: "smtp.gmail.com", smtpPort: 587 },
  "yahoo.com": { imapHost: "imap.mail.yahoo.com", imapPort: 993, smtpHost: "smtp.mail.yahoo.com", smtpPort: 465 },
  "yahoo.co.uk": { imapHost: "imap.mail.yahoo.com", imapPort: 993, smtpHost: "smtp.mail.yahoo.com", smtpPort: 465 },
  "ymail.com": { imapHost: "imap.mail.yahoo.com", imapPort: 993, smtpHost: "smtp.mail.yahoo.com", smtpPort: 465 },
  "outlook.com": { imapHost: "outlook.office365.com", imapPort: 993, smtpHost: "smtp.office365.com", smtpPort: 587 },
  "hotmail.com": { imapHost: "outlook.office365.com", imapPort: 993, smtpHost: "smtp.office365.com", smtpPort: 587 },
  "live.com": { imapHost: "outlook.office365.com", imapPort: 993, smtpHost: "smtp.office365.com", smtpPort: 587 },
  "icloud.com": { imapHost: "imap.mail.me.com", imapPort: 993, smtpHost: "smtp.mail.me.com", smtpPort: 587 },
  "me.com": { imapHost: "imap.mail.me.com", imapPort: 993, smtpHost: "smtp.mail.me.com", smtpPort: 587 },
  "aol.com": { imapHost: "imap.aol.com", imapPort: 993, smtpHost: "smtp.aol.com", smtpPort: 465 },
  "protonmail.com": { imapHost: "127.0.0.1", imapPort: 1143, smtpHost: "127.0.0.1", smtpPort: 1025 }, // ProtonMail Bridge
  "proton.me": { imapHost: "127.0.0.1", imapPort: 1143, smtpHost: "127.0.0.1", smtpPort: 1025 },
};

function getProviderSettings(email: string) {
  const domain = email.split("@")[1]?.toLowerCase();
  return domain ? EMAIL_PROVIDER_SETTINGS[domain] : undefined;
}

function getEmailAccounts(): EmailAccount[] {
  try {
    const stored = localStorage.getItem("moby_pref_emailAccounts");
    if (stored) {
      const accounts = JSON.parse(stored) as EmailAccount[];
      // Migrate older accounts that don't have authType
      return accounts.map(account => ({
        ...account,
        authType: account.authType || "password",
      }));
    }
  } catch {
    // ignore
  }
  return [];
}

function saveEmailAccounts(accounts: EmailAccount[]): void {
  try {
    localStorage.setItem("moby_pref_emailAccounts", JSON.stringify(accounts));
    // Notify other components (like chat-list-panel) that accounts changed
    window.dispatchEvent(new CustomEvent("user-preferences-updated"));
  } catch (error) {
    console.error("Failed to save email accounts:", error);
  }
}

// Known provider presets for the IMAP/SMTP dropdown
const PROVIDER_PRESETS: { label: string; domain: string }[] = [
  { label: "Gmail / Google Workspace", domain: "gmail.com" },
  { label: "Outlook / Hotmail / Live", domain: "outlook.com" },
  { label: "Yahoo Mail", domain: "yahoo.com" },
  { label: "iCloud / Me.com", domain: "icloud.com" },
  { label: "AOL Mail", domain: "aol.com" },
  { label: "ProtonMail (Bridge)", domain: "protonmail.com" },
  { label: "Custom / Manual", domain: "" },
];

function AccountEditor({
  account,
  onSave,
  onCancel,
  onDelete,
  gatewayUrl,
  session,
  usedColors,
}: {
  account: EmailAccount;
  onSave: (account: EmailAccount) => void;
  onCancel: () => void;
  onDelete?: () => void;
  gatewayUrl: string;
  session: Session | null | undefined;
  usedColors: Set<string>;
}) {
  const [form, setForm] = useState<EmailAccount>(account);
  const [testStatus, setTestStatus] = useState<"idle" | "testing" | "success" | "error">("idle");
  const [testMessage, setTestMessage] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "validating" | "error">("idle");
  const [oauthStatus, setOauthStatus] = useState<"idle" | "pending" | "success" | "error">("idle");
  const [oauthConfigured, setOauthConfigured] = useState(false);
  const [registerStatus, setRegisterStatus] = useState<"idle" | "registering" | "success" | "error">("idle");
  const [registerMessage, setRegisterMessage] = useState("");

  const updateField = <K extends keyof EmailAccount>(key: K, value: EmailAccount[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // Check if Google OAuth is configured on mount
  useEffect(() => {
    if (!gatewayUrl) return;
    fetch(`${gatewayUrl}/api/email/oauth/google/config`)
      .then(res => res.json())
      .then(data => setOauthConfigured(data.configured))
      .catch(() => setOauthConfigured(false));
  }, [gatewayUrl]);

  // Handle OAuth redirect return — pick up stashed OAuth code from localStorage
  // This runs when the component mounts after Google OAuth redirects back through
  // /onboarding (which forwards logged-in users to /chats with code stashed in localStorage)
  useEffect(() => {
    const oauthReturn = localStorage.getItem('moby-oauth-return');
    if (oauthReturn !== 'email-drawer') return;

    const oauthCode = localStorage.getItem('moby-oauth-code');
    const oauthError = localStorage.getItem('moby-oauth-error');
    const receivedState = localStorage.getItem('moby-oauth-state-received');
    const expectedState = localStorage.getItem('moby-oauth-state');

    // Clean up all OAuth localStorage keys
    localStorage.removeItem('moby-oauth-return');
    localStorage.removeItem('moby-oauth-code');
    localStorage.removeItem('moby-oauth-error');
    localStorage.removeItem('moby-oauth-state-received');
    localStorage.removeItem('moby-oauth-state');

    if (oauthError) {
      setOauthStatus("error");
      setTestMessage(oauthError);
      return;
    }

    if (!oauthCode) return;

    // Verify state
    if (receivedState !== expectedState) {
      setOauthStatus("error");
      setTestMessage("OAuth state mismatch — please try again");
      return;
    }

    // Exchange code for tokens
    setOauthStatus("pending");
    setTestMessage("Completing Google Sign-In...");

    (async () => {
      try {
        const tokenResponse = await fetch(`${gatewayUrl}/api/email/oauth/google/token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: oauthCode }),
        });

        const tokenData = await tokenResponse.json();

        if (tokenData.success) {
          setForm(prev => ({
            ...prev,
            email: tokenData.email,
            username: tokenData.email,
            accessToken: tokenData.accessToken,
            refreshToken: tokenData.refreshToken,
            tokenExpiry: Date.now() + (tokenData.expiresIn * 1000),
            authType: "oauth2" as const,
            imapHost: "imap.gmail.com",
            imapPort: 993,
            smtpHost: "smtp.gmail.com",
            smtpPort: 587,
          }));
          setOauthStatus("success");
          setTestMessage(`Signed in as ${tokenData.email}`);
        } else {
          setOauthStatus("error");
          setTestMessage(tokenData.error || "OAuth failed");
        }
      } catch (err) {
        setOauthStatus("error");
        setTestMessage(err instanceof Error ? err.message : "Token exchange failed");
      }
    })();
  }, [gatewayUrl]);

  // Start Google OAuth — same-tab redirect (matches working onboarding flow)
  // Previously used popup (window.open) which gets blocked by popup blockers,
  // silently failing and leaving accounts without OAuth tokens
  const startGoogleOAuth = useCallback(async () => {
    if (!gatewayUrl) {
      setTestStatus("error");
      setTestMessage("Configure Gateway URL in Settings first");
      return;
    }

    setOauthStatus("pending");
    setTestMessage("Starting Google Sign-In...");

    try {
      const response = await fetch(`${gatewayUrl}/api/email/oauth/google/auth-url`);
      const data = await response.json();

      if (!data.success || !data.authUrl) {
        setOauthStatus("error");
        setTestMessage(data.error || "Failed to get OAuth URL");
        return;
      }

      // Save state + return marker in localStorage before redirect
      // OnboardingPage will detect the return marker and stash the OAuth code
      // for this component to pick up on mount (see useEffect above)
      localStorage.setItem('moby-oauth-state', data.state);
      localStorage.setItem('moby-oauth-return', 'email-drawer');
      localStorage.setItem('moby-oauth-account-id', form.id);

      // Redirect to Google (same tab) — callback redirects to /onboarding,
      // which forwards logged-in users to /chats with OAuth code stashed
      window.location.href = data.authUrl;
    } catch (error) {
      setOauthStatus("error");
      setTestMessage(error instanceof Error ? error.message : "OAuth failed");
    }
  }, [gatewayUrl]);

  const testImapConnection = async (): Promise<boolean> => {
    if (!gatewayUrl) {
      setTestStatus("error");
      setTestMessage("Configure Gateway URL in Settings first");
      return false;
    }

    setTestStatus("testing");
    setTestMessage("Testing IMAP...");

    try {
      const credentials: { user: string; pass?: string; accessToken?: string } = {
        user: form.username,
      };

      if (form.authType === "oauth2" && form.accessToken) {
        credentials.accessToken = form.accessToken;
      } else {
        credentials.pass = form.password;
      }

      const response = await fetch(`${gatewayUrl}/api/email/imap/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          host: form.imapHost,
          port: form.imapPort,
          secure: true,
          credentials,
        }),
      });

      const data = await response.json().catch(() => ({ success: false, error: "Invalid response" }));
      
      if (response.ok && data.success) {
        setTestStatus("success");
        setTestMessage("IMAP connection successful!");
        return true;
      } else {
        setTestStatus("error");
        setTestMessage(data.error || `IMAP failed: ${response.status}`);
        return false;
      }
    } catch (error) {
      setTestStatus("error");
      setTestMessage(error instanceof Error ? error.message : "Connection failed");
      return false;
    }
  };

  // Register email with MobyChat directory (MongoDB)
  const registerEmailWithMobyChat = async (email: string, isPrimary: boolean): Promise<boolean> => {
    console.log("[EmailAccount] registerEmailWithMobyChat called", { email, gatewayUrl: gatewayUrl || "(empty)" });
    if (!gatewayUrl) {
      console.warn("[EmailAccount] No gateway URL configured - skipping MongoDB registration");
      return false;
    }
    
    try {
      setRegisterStatus("registering");
      setRegisterMessage("Registering email with MobyChat...");
      
      // Get the user's moby address and secret from session (IndexedDB via provider)
      const mobyAddress = session?.address;
      const mobySecret = session?.secret;
      
      if (!mobyAddress || !mobySecret) {
        console.warn("[EmailAccount] Session missing - address:", !!mobyAddress, "secret:", !!mobySecret);
        setRegisterStatus("error");
        setRegisterMessage("Not logged in to MobyChat");
        return false;
      }
      
      // Create signed request (required by server auth middleware)
      // Server expects base64 encoding via tweetnacl-util's decodeBase64
      const timestamp = Date.now().toString();
      const requestBody = JSON.stringify({ email, isPrimary });
      const messageToSign = `${timestamp}${requestBody}`;
      const messageBytes = new TextEncoder().encode(messageToSign);
      const signature = ed25519.sign(messageBytes, hexToBytes(mobySecret));
      const signatureBase64 = encodeBase64(signature);
      const addressBase64 = encodeBase64(hexToBytes(mobyAddress));
      
      console.log("[EmailAccount] Registering email with MobyChat", { 
        email, 
        gatewayUrl,
        addressLength: hexToBytes(mobyAddress).length,
        signatureLength: signature.length 
      });
      
      const response = await fetch(`${gatewayUrl}/api/user/register-email`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "X-Moby-Address": addressBase64,
          "X-Moby-Timestamp": timestamp,
          "X-Moby-Signature": signatureBase64,
        },
        body: requestBody,
      });
      
      console.log("[EmailAccount] Registration response:", { 
        status: response.status, 
        ok: response.ok 
      });
      
      const data = await response.json().catch(() => ({ success: false, error: "Invalid response" }));
      console.log("[EmailAccount] Registration data:", data);
      
      if (response.ok && data.success) {
        console.log("[EmailAccount] ✓ Email registered with MobyChat successfully");
        setRegisterStatus("success");
        setRegisterMessage("Email registered with MobyChat!");
        return true;
      } else {
        console.warn("[EmailAccount] Registration failed:", data.error || response.status);
        setRegisterStatus("error");
        setRegisterMessage(data.error || "Failed to register email");
        return false;
      }
    } catch (error) {
      console.error("[EmailAccount] Registration exception:", error);
      setRegisterStatus("error");
      setRegisterMessage(error instanceof Error ? error.message : "Registration failed");
      return false;
    }
  };

  // Validate auth and save - called when Save is clicked
  const handleSave = async () => {
    // Check if we have credentials to validate
    const hasCredentials = form.email && (form.password || form.accessToken);
    if (!hasCredentials) {
      setTestStatus("error");
      setTestMessage("Enter email and password first");
      return;
    }

    setSaveStatus("validating");
    setTestMessage("Validating credentials...");
    setTestStatus("testing");

    const isValid = await testImapConnection();
    
    if (isValid) {
      // If registration is enabled, register with MobyChat
      if (form.registeredWithMobyChat) {
        const registered = await registerEmailWithMobyChat(form.email, form.isPrimary || false);
        if (!registered) {
          // Registration failed but IMAP is valid - allow save anyway with warning
          console.warn("[EmailAccount] MongoDB registration failed, saving locally only");
        }
      }
      
      setSaveStatus("idle");
      onSave(form);
    } else {
      setSaveStatus("error");
      // testMessage already set by testImapConnection
    }
  };

  // Check if form has minimum required fields to attempt save
  const canAttemptSave = Boolean(
    form.email && 
    (form.password || form.accessToken) &&
    form.imapHost && form.imapPort &&
    form.smtpHost && form.smtpPort
  );

  const inputClass = "w-full px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary";

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between pb-3 flex-shrink-0">
        <div className="text-title-md">{account.email ? "Edit Account" : "Add Account"}</div>
        <button onClick={onCancel} className="text-label-sm text-on-surface-variant hover:text-on-surface">
          Cancel
        </button>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 pb-4">
        {/* ── Email Address ── */}
        <div className="space-y-1">
          <label className="text-label-sm text-on-surface-variant block">Email Address</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => {
              const email = e.target.value;
              updateField("email", email);
              updateField("username", email);
              // Auto-detect provider settings
              const settings = getProviderSettings(email);
              if (settings) {
                setForm(prev => ({ ...prev, email, username: email, ...settings }));
              }
            }}
            placeholder="you@example.com"
            className={inputClass}
          />
        </div>

        {/* ── Password / App Password ── */}
        <div className="space-y-1">
          <label className="text-label-sm text-on-surface-variant block">Password / App Password</label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => updateField("password", e.target.value)}
            placeholder="••••••••"
            className={inputClass}
          />
          {(form.imapHost?.includes("gmail") || form.email?.includes("@gmail") || form.email?.includes("@mobynet.io")) && (
            <div className="text-label-sm text-on-surface-variant bg-surface-container p-2 rounded-lg mt-1">
              <strong>Gmail/Google Workspace:</strong> Use an{" "}
              <a 
                href="https://myaccount.google.com/apppasswords" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                App Password
              </a>
              , not your regular password. Requires 2FA enabled.
            </div>
          )}
        </div>

        {/* ── Register / Primary — inline compact checkboxes ── */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.registeredWithMobyChat || false}
              onChange={(e) => updateField("registeredWithMobyChat", e.target.checked)}
              className="w-4 h-4 rounded border-outline-variant text-primary focus:ring-primary"
            />
            <span className="text-label-sm text-on-surface">Register with MobyNet</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isPrimary || false}
              onChange={(e) => updateField("isPrimary", e.target.checked)}
              className="w-4 h-4 rounded border-outline-variant text-primary focus:ring-primary"
            />
            <span className="text-label-sm text-on-surface">Primary</span>
          </label>
        </div>

        {registerStatus !== "idle" && (
          <div className={`text-body-sm px-3 py-2 rounded-lg ${
            registerStatus === "success" ? "bg-green-500/10 text-green-600 dark:text-green-400" :
            registerStatus === "error" ? "bg-red-500/10 text-red-600 dark:text-red-400" :
            "bg-primary/10 text-primary"
          }`}>
            {registerMessage}
          </div>
        )}

        {/* ── Display Color — small squares, gray out in-use ── */}
        <div className="space-y-1">
          <label className="text-label-sm text-on-surface-variant block">Display Color</label>
          <div className="flex gap-1.5 flex-wrap">
            {DEFAULT_COLORS.map((color) => {
              const isUsed = usedColors.has(color) && form.color !== color;
              return (
                <button
                  key={color}
                  onClick={() => { if (!isUsed) updateField("color", color); }}
                  className={`w-6 h-6 rounded transition-all ${
                    form.color === color
                      ? "ring-2 ring-primary ring-offset-1 ring-offset-surface-container-high"
                      : isUsed
                        ? "opacity-25 cursor-not-allowed"
                        : "hover:scale-110"
                  }`}
                  style={{ backgroundColor: color }}
                  title={isUsed ? `In use` : color}
                  disabled={isUsed}
                />
              );
            })}
          </div>
        </div>

        {/* ── Google OAuth alternative ── */}
        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 border-t border-outline-variant" />
          <span className="text-label-sm text-on-surface-variant">or</span>
          <div className="flex-1 border-t border-outline-variant" />
        </div>

        <div className="space-y-2">
          {oauthConfigured ? (
            <button
              onClick={() => {
                updateField("authType", "oauth2");
                startGoogleOAuth();
              }}
              disabled={oauthStatus === "pending"}
              className="w-full py-2 px-4 bg-surface-container-highest hover:bg-surface-container rounded-lg text-label-md flex items-center justify-center gap-2 border border-outline-variant"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {oauthStatus === "pending" ? "Signing in..." : "Sign in with Google"}
            </button>
          ) : (
            <div className="text-body-sm text-on-surface-variant text-center py-3 bg-surface-container rounded-lg">
              Google OAuth not configured on server.
            </div>
          )}
          {form.accessToken && (
            <div className="text-body-sm text-tertiary">
              Authenticated as {form.email}
            </div>
          )}
        </div>

        {/* ── Manual Email Gateways — IMAP / SMTP ── */}
        <div className="border-t border-outline-variant pt-3 mt-3">
          <div className="text-label-md text-on-surface-variant mb-2">Manual Email Gateways</div>

          {/* Provider preset dropdown */}
          <div className="space-y-1 mb-3">
            <label className="text-label-sm text-on-surface-variant block">Email Provider</label>
            <select
              value={
                PROVIDER_PRESETS.find(p => p.domain && EMAIL_PROVIDER_SETTINGS[p.domain]?.imapHost === form.imapHost)?.domain || ""
              }
              onChange={(e) => {
                const domain = e.target.value;
                if (domain && EMAIL_PROVIDER_SETTINGS[domain]) {
                  const settings = EMAIL_PROVIDER_SETTINGS[domain];
                  setForm(prev => ({ ...prev, ...settings }));
                }
              }}
              title="Select email provider"
              className={inputClass}
            >
              {PROVIDER_PRESETS.map((p) => (
                <option key={p.domain} value={p.domain}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="text-label-sm text-on-surface-variant mb-1">IMAP Settings</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="space-y-1">
              <label className="text-label-sm text-on-surface-variant block">Host</label>
              <input
                type="text"
                value={form.imapHost}
                onChange={(e) => updateField("imapHost", e.target.value)}
                placeholder="imap.gmail.com"
                className={inputClass}
              />
            </div>
            <div className="space-y-1">
              <label className="text-label-sm text-on-surface-variant block">Port</label>
              <input
                type="number"
                value={form.imapPort}
                onChange={(e) => updateField("imapPort", Number(e.target.value))}
                placeholder="993"
                className={inputClass}
              />
            </div>
          </div>

          <div className="text-label-sm text-on-surface-variant mb-1">SMTP Settings</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-label-sm text-on-surface-variant block">Host</label>
              <input
                type="text"
                value={form.smtpHost}
                onChange={(e) => updateField("smtpHost", e.target.value)}
                placeholder="smtp.gmail.com"
                className={inputClass}
              />
            </div>
            <div className="space-y-1">
              <label className="text-label-sm text-on-surface-variant block">Port</label>
              <input
                type="number"
                value={form.smtpPort}
                onChange={(e) => updateField("smtpPort", Number(e.target.value))}
                placeholder="587"
                className={inputClass}
              />
            </div>
          </div>
        </div>

      </div>

      <div className="flex-shrink-0 pt-3 border-t border-outline-variant mt-auto">
        {testStatus !== "idle" && (
          <div className={`text-body-sm px-3 py-2 rounded-lg mb-2 ${
            testStatus === "success" ? "bg-green-500/10 text-green-600 dark:text-green-400" :
            testStatus === "error" ? "bg-red-500/10 text-red-600 dark:text-red-400" :
            "bg-primary/10 text-primary"
          }`}>
            {testMessage}
          </div>
        )}
        <div className="flex gap-2">
          <mui.Button
            variant="secondary"
            onClick={() => testImapConnection()}
            disabled={testStatus === "testing" || saveStatus === "validating"}
            className="flex-1"
          >
            Test IMAP
          </mui.Button>
          <mui.Button
            variant="primary"
            filled
            onClick={handleSave}
            disabled={!canAttemptSave || saveStatus === "validating"}
            className="flex-1"
          >
            {saveStatus === "validating" ? "Validating..." : "Save"}
          </mui.Button>
        </div>
        {onDelete && (
          <button
            onClick={onDelete}
            className="w-full text-label-sm text-red-600 dark:text-red-400 hover:underline py-2 mt-2"
          >
            Delete Account
          </button>
        )}
      </div>
    </div>
  );
}

export function EmailDrawer({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  children: ReactNode | ((props: { unseenCount: number }) => ReactNode);
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const session = useSession();
  const db = useMobyDB();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setIsOpen = (open: boolean) => {
    setUncontrolledOpen(open);
    controlledOnOpenChange?.(open);
  };
  const [activeTab, setActiveTab] = useState<string>("external");

  // Gateway settings
  const [emailGatewayUrl, setEmailGatewayUrl] = useState<string>("");
  const [emailGatewayPollIntervalMs, setEmailGatewayPollIntervalMs] = useState<string>("");

  // Multi-account support
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null);
  const [isAddingAccount, setIsAddingAccount] = useState(false);

  // Test connection state
  const [testStatus, setTestStatus] = useState<"idle" | "testing" | "success" | "error">("idle");
  const [testMessage, setTestMessage] = useState<string>("");

  // Compose state
  const [composeTo, setComposeTo] = useState<string>("");
  const [composeSubject, setComposeSubject] = useState<string>("");
  const [composeBody, setComposeBody] = useState<string>("");
  const [composeFromAccount, setComposeFromAccount] = useState<string>("");
  const [sendStatus, setSendStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [sendMessage, setSendMessage] = useState<string>("");
  
  // Attachment routing state
  const [composeAttachments, setComposeAttachments] = useState<File[]>([]);
  const [recipientLookup, setRecipientLookup] = useState<{
    status: "idle" | "checking" | "found" | "not_found";
    user: { publicKey: string; username?: string; preferredName?: string; presence?: { status: string } } | null;
  }>({ status: "idle", user: null });
  const [attachmentRouting, setAttachmentRouting] = useState<"inline" | "fileTransfer">("fileTransfer");

  // Load settings when drawer opens (or on OAuth return — accounts must load before E5 auto-open)
  const hasOauthReturn = typeof window !== 'undefined' && localStorage.getItem('moby-oauth-return') === 'email-drawer';
  useEffect(() => {
    if (!isOpen && !hasOauthReturn) return;

    // Auto-resolve gateway URL from current origin if not set
    const storedUrl = getLocalPreference("emailGatewayUrl");
    if (storedUrl) {
      setEmailGatewayUrl(storedUrl);
    } else {
      // Default to same origin as the app (email gateway runs alongside signaling server)
      const defaultUrl = window.location.origin;
      setEmailGatewayUrl(defaultUrl);
      setLocalPreference("emailGatewayUrl", defaultUrl);
    }

    const interval = getLocalPreference("emailGatewayPollIntervalMs");
    setEmailGatewayPollIntervalMs(interval ?? "10000");

    // Load accounts from IndexedDB (wallet-scoped, async)
    let cancelled = false;
    getEmailAccountsFromDB(db).then(loadedAccounts => {
      if (cancelled) return;
      setAccounts(loadedAccounts);
      // Always ensure composeFromAccount points to a valid enabled account
      const enabledAccounts = loadedAccounts.filter(a => a.enabled);
      if (enabledAccounts.length > 0) {
        const currentValid = enabledAccounts.find(a => a.id === composeFromAccount);
        if (!currentValid) {
          setComposeFromAccount(enabledAccounts[0].id);
        }
      }
    });
    return () => { cancelled = true; };
  }, [isOpen, composeFromAccount, db]);

  useEffect(() => {
    const open = (e: Event) => {
      setIsOpen(true);
      // Support opening directly to a specific account's editor (used by E2 re-auth button)
      const detail = (e as CustomEvent).detail;
      if (detail?.editAccountId) {
        const target = accounts.find(a => a.id === detail.editAccountId);
        if (target) {
          setEditingAccount(target);
          setIsAddingAccount(false);
        }
      }
    };
    window.addEventListener("open-email-drawer", open);
    return () => window.removeEventListener("open-email-drawer", open);
  }, [accounts]);

  // E5: Auto-open drawer + editor on OAuth redirect return
  // After Google OAuth redirect, the page reloads and all React state is lost.
  // Detect the return marker and auto-open the editor so AccountEditor mounts
  // and its useEffect can process the stashed OAuth code.
  useEffect(() => {
    const oauthReturn = localStorage.getItem('moby-oauth-return');
    if (oauthReturn !== 'email-drawer') return;
    const accountId = localStorage.getItem('moby-oauth-account-id');
    if (!accountId) return;

    // Wait for accounts to load from IndexedDB before opening editor
    if (accounts.length === 0) return;

    const target = accounts.find(a => a.id === accountId);
    if (target) {
      console.log('[EmailDrawer] OAuth return detected — auto-opening editor for', target.email);
      setIsOpen(true);
      setActiveTab('external');
      setEditingAccount(target);
      setIsAddingAccount(false);
    } else {
      // Account not found — might be a new account being added, create a stub
      console.log('[EmailDrawer] OAuth return detected — account not found, opening as new');
      setIsOpen(true);
      setActiveTab('external');
      setEditingAccount({
        id: accountId,
        email: '',
        imapHost: '',
        imapPort: 993,
        smtpHost: '',
        smtpPort: 587,
        username: '',
        password: '',
        color: DEFAULT_COLORS[accounts.length % DEFAULT_COLORS.length],
        enabled: true,
        authType: 'oauth2',
      });
      setIsAddingAccount(true);
    }
    // Clean up the account-id marker (oauth-return stays for AccountEditor's useEffect)
    localStorage.removeItem('moby-oauth-account-id');
  }, [accounts]);

  const unseenCount = 0;

  const saveSettings = useCallback(() => {
    const trimmedUrl = emailGatewayUrl.trim();
    if (trimmedUrl.length > 0) {
      setLocalPreference("emailGatewayUrl", trimmedUrl);
    } else {
      localStorage.removeItem("moby_pref_emailGatewayUrl");
    }

    const trimmedInterval = emailGatewayPollIntervalMs.trim();
    if (trimmedInterval.length > 0) {
      const parsed = Number(trimmedInterval);
      if (Number.isFinite(parsed) && parsed > 0) {
        setLocalPreference("emailGatewayPollIntervalMs", String(parsed));
      }
    } else {
      localStorage.removeItem("moby_pref_emailGatewayPollIntervalMs");
    }

    window.dispatchEvent(
      new CustomEvent("user-preferences-updated", {
        detail: {
          emailGatewayUrl: emailGatewayUrl.trim(),
          emailGatewayPollIntervalMs,
        },
      }),
    );
  }, [emailGatewayUrl, emailGatewayPollIntervalMs]);

  const testConnection = useCallback(async () => {
    const gatewayUrl = emailGatewayUrl.trim();
    if (!gatewayUrl) {
      setTestStatus("error");
      setTestMessage("Gateway URL is required");
      return;
    }

    setTestStatus("testing");
    setTestMessage("Testing connection...");

    try {
      const response = await fetch(`${gatewayUrl}/api/email/health`);
      if (response.ok) {
        setTestStatus("success");
        setTestMessage("Connection successful!");
      } else {
        setTestStatus("error");
        setTestMessage(`Server returned ${response.status}`);
      }
    } catch (error) {
      setTestStatus("error");
      setTestMessage(error instanceof Error ? error.message : "Connection failed");
    }
  }, [emailGatewayUrl]);

  // Lookup recipient email to check if they have MobyChat
  const lookupRecipient = useCallback(async (email: string) => {
    if (!email || !email.includes("@")) {
      setRecipientLookup({ status: "idle", user: null });
      return;
    }

    const gatewayUrl = emailGatewayUrl.trim();
    if (!gatewayUrl) return;

    setRecipientLookup({ status: "checking", user: null });

    try {
      const response = await fetch(`${gatewayUrl}/api/user/lookup-email?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.success && data.found) {
        setRecipientLookup({ status: "found", user: data.user });
      } else {
        setRecipientLookup({ status: "not_found", user: null });
      }
    } catch {
      setRecipientLookup({ status: "not_found", user: null });
    }
  }, [emailGatewayUrl]);

  // Debounced recipient lookup when To field changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (composeTo.trim()) {
        lookupRecipient(composeTo.trim());
      } else {
        setRecipientLookup({ status: "idle", user: null });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [composeTo, lookupRecipient]);

  // Handle file attachment
  const handleAttachFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setComposeAttachments(prev => [...prev, ...Array.from(files)]);
    }
    e.target.value = ""; // Reset input
  }, []);

  // Remove attachment
  const removeAttachment = useCallback((index: number) => {
    setComposeAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const addAccount = useCallback(() => {
    // Auto-select the next available (unused) color
    const usedColorSet = new Set(accounts.map(a => a.color));
    const nextColor = DEFAULT_COLORS.find(c => !usedColorSet.has(c)) || DEFAULT_COLORS[accounts.length % DEFAULT_COLORS.length];

    const newAccount: EmailAccount = {
      id: crypto.randomUUID(),
      email: "",
      imapHost: "imap.gmail.com",
      imapPort: 993,
      smtpHost: "smtp.gmail.com",
      smtpPort: 587,
      username: "",
      password: "",
      color: nextColor,
      enabled: true,
      authType: "password",
    };
    setEditingAccount(newAccount);
    setIsAddingAccount(true);
  }, [accounts]);

  const saveAccount = useCallback((account: EmailAccount) => {
    let newAccounts: EmailAccount[];
    if (isAddingAccount) {
      newAccounts = [...accounts, account];
    } else {
      newAccounts = accounts.map(a => a.id === account.id ? account : a);
    }
    setAccounts(newAccounts);
    // Persist to IndexedDB (wallet-scoped) + backward-compat localStorage sync
    void saveAllEmailAccountsToDB(db, newAccounts);
    setEditingAccount(null);
    setIsAddingAccount(false);
  }, [accounts, isAddingAccount, db]);

  const deleteAccount = useCallback((accountId: string) => {
    const newAccounts = accounts.filter(a => a.id !== accountId);
    setAccounts(newAccounts);
    void deleteEmailAccountFromDB(db, accountId);
    setEditingAccount(null);
  }, [accounts, db]);

  const toggleAccountEnabled = useCallback((accountId: string) => {
    const newAccounts = accounts.map(a => 
      a.id === accountId ? { ...a, enabled: !a.enabled } : a
    );
    setAccounts(newAccounts);
    void saveAllEmailAccountsToDB(db, newAccounts);
  }, [accounts, db]);

  const sendEmail = useCallback(async () => {
    const gatewayUrl = emailGatewayUrl.trim();
    // Find account by ID, or fallback to first enabled account if composeFromAccount is empty
    const enabledAccounts = accounts.filter(a => a.enabled);
    const account = composeFromAccount 
      ? accounts.find(a => a.id === composeFromAccount)
      : enabledAccounts[0];
    
    console.log('[sendEmail] gatewayUrl:', gatewayUrl, 'composeFromAccount:', composeFromAccount, 'account:', account?.email);
    
    if (!gatewayUrl || !account) {
      setSendStatus("error");
      setSendMessage(!gatewayUrl ? "Gateway URL not set - check Settings tab" : "No account selected");
      return;
    }

    if (!composeTo.trim() || !composeSubject.trim()) {
      setSendStatus("error");
      setSendMessage("To and Subject are required");
      return;
    }

    setSendStatus("sending");
    setSendMessage("Sending...");

    try {
      // Build credentials based on auth type
      const credentials: { user: string; pass?: string; accessToken?: string } = {
        user: account.username,
      };
      if (account.authType === "oauth2" && account.accessToken) {
        credentials.accessToken = account.accessToken;
      } else {
        credentials.pass = account.password;
      }

      // INLINE ATTACHMENTS: Base64-encode files for standard SMTP delivery
      let smtpAttachments: Array<{ filename: string; content: string }> = [];
      if (composeAttachments.length > 0) {
        const MAX_PER_FILE = 5 * 1024 * 1024;
        const MAX_TOTAL = 10 * 1024 * 1024;
        let totalSize = 0;
        
        for (const file of composeAttachments) {
          if (file.size > MAX_PER_FILE) {
            setSendStatus("error");
            setSendMessage(`${file.name} exceeds 5MB limit`);
            return;
          }
          totalSize += file.size;
        }
        if (totalSize > MAX_TOTAL) {
          setSendStatus("error");
          setSendMessage(`Total attachments (${(totalSize / (1024 * 1024)).toFixed(1)}MB) exceed 10MB limit`);
          return;
        }
        
        for (const file of composeAttachments) {
          try {
            const buffer = await file.arrayBuffer();
            const base64 = btoa(
              new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
            );
            smtpAttachments.push({ filename: file.name, content: base64 });
          } catch {
            setSendStatus("error");
            setSendMessage(`Failed to encode ${file.name}`);
            return;
          }
        }
      }

      const smtpPayload: Record<string, unknown> = {
        host: account.smtpHost,
        port: account.smtpPort,
        secure: account.smtpPort === 465,
        credentials,
        from: account.email,
        to: composeTo.trim(),
        subject: composeSubject.trim(),
        body: composeBody,
      };
      
      if (smtpAttachments.length > 0) {
        smtpPayload.attachments = smtpAttachments;
      }

      const response = await fetch(`${gatewayUrl}/api/email/smtp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(smtpPayload),
      });

      const data = await response.json().catch(() => ({ success: false, error: "Invalid response" }));
      
      if (response.ok && data.success) {
        setSendStatus("success");
        setSendMessage(smtpAttachments.length > 0 ? `Email sent with ${smtpAttachments.length} attachment(s)!` : "Email sent!");
        setComposeTo("");
        setComposeSubject("");
        setComposeBody("");
        setComposeAttachments([]);
      } else {
        setSendStatus("error");
        setSendMessage(data.error || `Failed: ${response.status}`);
      }
    } catch (error) {
      setSendStatus("error");
      setSendMessage(error instanceof Error ? error.message : "Send failed");
    }
  }, [emailGatewayUrl, accounts, composeFromAccount, composeTo, composeSubject, composeBody, composeAttachments]);

  return (
    <mui.Drawer.Root open={isOpen} onOpenChange={setIsOpen} direction="left">
      {controlledOpen === undefined ? (
        <mui.Drawer.Trigger asChild>
          {typeof children === "function" ? children({ unseenCount }) : children}
        </mui.Drawer.Trigger>
      ) : (
        typeof children === "function" ? children({ unseenCount }) : children
      )}
      <mui.Drawer.Portal>
        <mui.Drawer.Overlay className="sm:bg-transparent" />
        <mui.Drawer.Content className="bg-surface-container-high text-on-surface fixed inset-y-0 left-0 z-50 flex h-full w-full flex-col border-r border-outline sm:ml-20 sm:w-80 overflow-hidden">
          {/* Pointer arrow — visible on desktop */}
          <div className="hidden sm:block absolute -left-2 top-6 w-0 h-0 border-y-[8px] border-y-transparent border-r-[8px] border-r-surface-container-high" />
          <div className="border-b-outline flex items-center justify-between border-b p-4">
            <mui.Drawer.Title className="text-headline-sm">MobyMail™</mui.Drawer.Title>
            <button onClick={() => setIsOpen(false)} className="sm:hidden p-1 rounded-full hover:bg-on-surface-variant/8" aria-label="Close drawer">
              <XIcon size={20} />
            </button>
          </div>

          <mui.Tabs.Root
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex min-w-0 min-h-0 flex-1 flex-col"
          >
            <mui.Tabs.List>
              <mui.Tabs.Tab value="mobynet" disabled>
                MobyNet
              </mui.Tabs.Tab>
              <mui.Tabs.Tab value="external">External</mui.Tabs.Tab>
              <mui.Tabs.Tab value="compose">Compose</mui.Tabs.Tab>
              <mui.Tabs.Tab value="settings">Settings</mui.Tabs.Tab>
              <mui.Tabs.Indicator />
            </mui.Tabs.List>

            {/* External Tab - Connected Email Accounts */}
            <mui.Tabs.Panel value="external" className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {editingAccount ? (
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden p-4">
                  <AccountEditor
                    account={editingAccount}
                    onSave={saveAccount}
                    onCancel={() => { setEditingAccount(null); setIsAddingAccount(false); }}
                    onDelete={isAddingAccount ? undefined : () => deleteAccount(editingAccount.id)}
                    gatewayUrl={emailGatewayUrl}
                    session={session}
                    usedColors={new Set(accounts.filter(a => a.id !== editingAccount.id).map(a => a.color))}
                  />
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-title-md">Connected Accounts</div>
                    <mui.Button variant="primary" onClick={addAccount} className="text-label-sm">
                      + Add Account
                    </mui.Button>
                  </div>
                  
                  {accounts.length === 0 ? (
                    <div className="text-body-sm text-on-surface-variant py-8 text-center">
                      No email accounts connected.<br />
                      Click "Add Account" to get started.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {accounts.map((account) => (
                        <div
                          key={account.id}
                          className="flex gap-3 p-3 bg-surface-container rounded-lg border border-outline-variant"
                        >
                          <div
                            className="w-8 h-8 rounded-md flex-shrink-0 flex items-center justify-center"
                            style={{ backgroundColor: account.color }}
                          >
                            {getProviderLogo(account.email).Logo({ size: 18 })}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-body-md truncate">{account.email || "No email set"}</div>
                            <div className="flex items-center gap-1.5 mt-1">
                              {account.registeredWithMobyChat && (
                                <span className="text-label-sm px-1.5 py-0.5 rounded bg-secondary/20 text-secondary">
                                  Registered
                                </span>
                              )}
                              {account.isPrimary && (
                                <span className="text-label-sm px-1.5 py-0.5 rounded bg-tertiary/20 text-tertiary">
                                  Primary
                                </span>
                              )}
                              <div className="flex-1" />
                              <button
                                onClick={() => toggleAccountEnabled(account.id)}
                                className={`text-label-sm px-2 py-0.5 rounded ${
                                  account.enabled
                                    ? "bg-primary/10 text-primary"
                                    : "bg-surface-container-highest text-on-surface-variant"
                                }`}
                              >
                                {account.enabled ? "On" : "Off"}
                              </button>
                              <button
                                onClick={() => setEditingAccount(account)}
                                className="text-label-sm text-primary hover:underline"
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </mui.Tabs.Panel>

            {/* Compose Tab - Send Email */}
            <mui.Tabs.Panel value="compose" className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                <div className="text-title-md">Compose Email</div>
                
                {accounts.length === 0 ? (
                  <div className="text-body-sm text-on-surface-variant py-8 text-center">
                    Add an email account in the External tab first.
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <label className="text-label-sm text-on-surface-variant block">From</label>
                      <select
                        value={composeFromAccount}
                        onChange={(e) => setComposeFromAccount(e.target.value)}
                        title="Select sender account"
                        className="w-full px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-sm text-on-surface focus:outline-none focus:border-primary"
                      >
                        {accounts.filter(a => a.enabled).map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-label-sm text-on-surface-variant block">To</label>
                      <div className="relative">
                        <input
                          type="email"
                          value={composeTo}
                          onChange={(e) => setComposeTo(e.target.value)}
                          placeholder="recipient@example.com"
                          className="w-full px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary pr-24"
                        />
                        {/* Recipient status indicator */}
                        {recipientLookup.status !== "idle" && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2">
                            {recipientLookup.status === "checking" && (
                              <span className="text-label-sm text-on-surface-variant">Checking...</span>
                            )}
                            {recipientLookup.status === "found" && (
                              <span className="text-label-sm px-2 py-0.5 rounded bg-tertiary/20 text-tertiary">
                                MobyChat ✓
                              </span>
                            )}
                            {recipientLookup.status === "not_found" && (
                              <span className="text-label-sm px-2 py-0.5 rounded bg-surface-container-highest text-on-surface-variant">
                                External
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-label-sm text-on-surface-variant block">Subject</label>
                      <input
                        type="text"
                        value={composeSubject}
                        onChange={(e) => setComposeSubject(e.target.value)}
                        placeholder="Subject"
                        className="w-full px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-label-sm text-on-surface-variant block">Message</label>
                      <textarea
                        value={composeBody}
                        onChange={(e) => setComposeBody(e.target.value)}
                        placeholder="Type your message..."
                        rows={6}
                        className="w-full px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary resize-none"
                      />
                    </div>

                    {/* Attachments Section */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-label-sm text-on-surface-variant">Attachments</label>
                        <label className="text-label-sm text-primary cursor-pointer hover:underline">
                          + Add File
                          <input
                            type="file"
                            multiple
                            onChange={handleAttachFile}
                            className="hidden"
                          />
                        </label>
                      </div>
                      
                      {composeAttachments.length > 0 && (
                        <div className="space-y-2">
                          {composeAttachments.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-surface-container rounded-lg border border-outline-variant"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs text-primary">📎</span>
                                </div>
                                <div className="min-w-0">
                                  <div className="text-body-sm truncate">{file.name}</div>
                                  <div className="text-label-sm text-on-surface-variant">
                                    {(file.size / 1024).toFixed(1)} KB
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => removeAttachment(index)}
                                className="text-on-surface-variant hover:text-error p-1"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          
                          {/* Routing Summary */}
                          <div className="p-3 rounded-lg bg-surface-container-highest border border-outline-variant">
                            <div className="text-label-sm text-on-surface-variant mb-2">Attachment Routing</div>
                            <div className="flex items-center gap-2 mb-2">
                              <button
                                onClick={() => setAttachmentRouting("fileTransfer")}
                                className={`flex-1 text-label-sm px-3 py-1.5 rounded ${
                                  attachmentRouting === "fileTransfer"
                                    ? "bg-primary text-on-primary"
                                    : "bg-surface-container text-on-surface-variant"
                                }`}
                              >
                                P2P Transfer
                              </button>
                              <button
                                onClick={() => setAttachmentRouting("inline")}
                                className={`flex-1 text-label-sm px-3 py-1.5 rounded ${
                                  attachmentRouting === "inline"
                                    ? "bg-primary text-on-primary"
                                    : "bg-surface-container text-on-surface-variant"
                                }`}
                              >
                                Inline (SMTP)
                              </button>
                            </div>
                            
                            {/* Routing status info */}
                            {attachmentRouting === "fileTransfer" && (
                              <div className="text-body-sm">
                                {recipientLookup.status === "found" ? (
                                  <div className="flex items-center gap-2 text-tertiary">
                                    <span>✓</span>
                                    <span>
                                      Direct P2P transfer to {recipientLookup.user?.preferredName || "MobyChat user"}
                                      {recipientLookup.user?.presence?.status === "online" && (
                                        <span className="ml-1 text-green-500">(online)</span>
                                      )}
                                    </span>
                                  </div>
                                ) : recipientLookup.status === "not_found" ? (
                                  <div className="text-on-surface-variant">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span>⚠</span>
                                      <span>Recipient not on MobyChat</span>
                                    </div>
                                    <div className="text-label-sm">
                                      An invite link will be included. Files transfer when they join.
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-on-surface-variant">
                                    Enter recipient email to check routing
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {attachmentRouting === "inline" && (
                              <div className="text-body-sm text-on-surface-variant">
                                Files sent via traditional email (size limits apply)
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {sendStatus !== "idle" && (
                      <div className={`text-body-sm px-3 py-2 rounded-lg ${
                        sendStatus === "success" ? "bg-green-500/10 text-green-600 dark:text-green-400" :
                        sendStatus === "error" ? "bg-red-500/10 text-red-600 dark:text-red-400" :
                        "bg-primary/10 text-primary"
                      }`}>
                        {sendMessage}
                      </div>
                    )}

                    <mui.Button
                      variant="primary"
                      filled
                      onClick={sendEmail}
                      disabled={sendStatus === "sending"}
                      className="w-full"
                    >
                      {sendStatus === "sending" ? "Sending..." : "Send Email"}
                    </mui.Button>
                  </>
                )}
              </div>
            </mui.Tabs.Panel>

            {/* Settings Tab - Gateway Configuration */}
            <mui.Tabs.Panel value="settings" className="flex-1 overflow-y-auto p-4">
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="text-title-md">Gateway</div>
                  <div className="space-y-2">
                    <label className="text-label-sm text-on-surface-variant block">Gateway URL</label>
                    <input
                      type="text"
                      value={emailGatewayUrl}
                      onChange={(e) => setEmailGatewayUrl(e.target.value)}
                      placeholder="https://email-gateway.example.com"
                      className="w-full px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-label-sm text-on-surface-variant block">
                      Poll interval (ms)
                    </label>
                    <input
                      type="number"
                      value={emailGatewayPollIntervalMs}
                      onChange={(e) => setEmailGatewayPollIntervalMs(e.target.value)}
                      placeholder="15000"
                      className="w-full px-3 py-2 bg-surface-container border border-outline-variant rounded-lg text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                {testStatus !== "idle" && (
                  <div className={`text-body-sm px-3 py-2 rounded-lg ${
                    testStatus === "success" ? "bg-green-500/10 text-green-600 dark:text-green-400" :
                    testStatus === "error" ? "bg-red-500/10 text-red-600 dark:text-red-400" :
                    "bg-primary/10 text-primary"
                  }`}>
                    {testMessage}
                  </div>
                )}

                <div className="flex gap-2">
                  <mui.Button
                    variant="secondary"
                    onClick={testConnection}
                    disabled={testStatus === "testing"}
                    className="flex-1"
                  >
                    {testStatus === "testing" ? "Testing..." : "Test Connection"}
                  </mui.Button>
                  <mui.Button
                    variant="primary"
                    filled
                    onClick={() => {
                      saveSettings();
                      setIsOpen(false);
                    }}
                    className="flex-1"
                  >
                    Save
                  </mui.Button>
                </div>
              </div>
            </mui.Tabs.Panel>
          </mui.Tabs.Root>
        </mui.Drawer.Content>
      </mui.Drawer.Portal>
    </mui.Drawer.Root>
  );
}
